import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PayPalBox({ buildPayload, disabled, onPaid, testId = "paypal-buttons" }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  if (!config) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div
        data-testid="paypal-not-configured"
        className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs leading-relaxed text-slate-500"
      >
        Online PayPal &amp; card payments are being set up. In the meantime, call or text to book —
        we confirm everything personally.
      </div>
    );
  }

  return (
    <div data-testid={testId} className={disabled ? "pointer-events-none opacity-50" : ""}>
      <PayPalScriptProvider
        options={{ clientId: config.client_id, currency: "USD", intent: "capture" }}
      >
        <PayPalButtons
          style={{ layout: "vertical", shape: "pill", label: "pay" }}
          forceReRender={[disabled]}
          createOrder={async () => {
            const res = await axios.post(`${API}/payments/orders`, buildPayload());
            return res.data.order_id;
          }}
          onApprove={async (data) => {
            try {
              const res = await axios.post(`${API}/payments/orders/${data.orderID}/capture`);
              if (res.data.status === "COMPLETED") {
                onPaid(res.data);
              } else {
                toast.error("Payment was not completed.", {
                  description: "No charge was finalized — please try again or call us.",
                });
              }
            } catch {
              toast.error("We couldn't confirm your payment.", {
                description: "If you were charged, contact us with your PayPal receipt.",
              });
            }
          }}
          onError={() =>
            toast.error("PayPal checkout hit a problem.", {
              description: "Please try again, or call/text to book instead.",
            })
          }
        />
      </PayPalScriptProvider>
      <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout by PayPal — cards accepted, no
        PayPal account needed
      </p>
    </div>
  );
}
